import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Breakpoint,
} from '@mui/material';

interface FormDialogProps {
  open: boolean;
  title: React.ReactNode;
  onClose: () => void;
  onSubmit: () => void;
  children: React.ReactNode;
  submitText?: string;
  cancelText?: string;
  submitting?: boolean;
  disableSubmit?: boolean;
  maxWidth?: Breakpoint;
  /** Hide the default footer (when the form renders its own actions). */
  hideActions?: boolean;
}

/**
 * Consistent modal wrapper for create/edit forms: title, scrollable content,
 * and a standard Cancel/Submit footer with a busy state. Submitting via Enter
 * is supported through a wrapping <form>.
 */
const FormDialog: React.FC<FormDialogProps> = ({
  open,
  title,
  onClose,
  onSubmit,
  children,
  submitText = 'Save',
  cancelText = 'Cancel',
  submitting = false,
  disableSubmit = false,
  maxWidth = 'sm',
  hideActions = false,
}) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={maxWidth}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!submitting && !disableSubmit) onSubmit();
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
        <DialogContent dividers>{children}</DialogContent>
        {!hideActions && (
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={onClose} color="inherit" disabled={submitting}>
              {cancelText}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || disableSubmit}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {submitText}
            </Button>
          </DialogActions>
        )}
      </form>
    </Dialog>
  );
};

export default FormDialog;
